import * as React from 'react';
import { css } from '@emotion/core';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { btnStyle } from '../../forms/styles/button';
import { COLORS } from '../../globalStyle/colors';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import LocalDiv from '../../localization/LocalDiv';
import Msg from '../../localization/Msg';
import { Str } from '../../localization/translationKeys';
import LocalInput from '../../localization/LocalInput';
import { INPUT } from '../../forms/styles/input';
import LocalBtn from '../../localization/LocalBtn';
import { ERROR_MSG } from '../../forms/styles/messages';
import { useError, useLoading } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import { sendGoalsAction } from './sendGoalsAction';
import { SendTo } from './models';
import { emailGoalsFormValidation } from './formValidations';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spacer from '../../layout/Spacer';
import { FIELDSET_DEFAULT } from '../../layout/fieldSetStyle';
import LocalTextArea from '../../localization/LocalTextArea';
import { useToggle } from '../../core/hooks/useToggle';
import { useStickyDiff } from '../../core/hooks/useStickyDiff';
import { onPhone } from '../../globalStyle/mediaQueries';
import { useProjIds } from '../../navigation/hooks';


const styles = {
  root:         [
    css`
      width: 320px;
      ${onPhone} {
        width: auto;
      }    `,
  ],
  msgRow:       [
    css`
      margin: 0 0 20px;
    `,
  ],
  input:        [
    css`
      margin-bottom: 10px;
      border: none;
      width: 100%;
    `,
  ],
  textArea:     [
    css`
      margin-bottom: 15px;
      padding: 10px;
      color: #474747;
      box-sizing: border-box;
      border: none;
      border-radius: 8px;
      width: 100%;
    `,
  ],
  submitButton: [
    btnStyle.primary(COLORS.DARK_GREY),
    css`
      width: 100%;
    `,
  ],
};

type Props = {
  className?: string;
  languageId: number;
  allAnswered: boolean;
};


/**
 * EmailGoalsForm: a component that allows goals to be sent via email
 *
 * PureComponent: true
 * Redux Connected: false
 */
const EmailGoalsForm: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const projIds = useProjIds();

  const sendingGoals      = useLoading(ActionType.SendGoalsRequest, `${(projIds.projId)}`);
  const errorSendingGoals = !!useError(ActionType.SendGoalsRequest, `${(projIds.projId)}`);

  const [goalsSent, toggleSentAlert] = useToggle();

  useStickyDiff(
    (prev, next) => prev?.isLoading && !next.isLoading && !next.hasError && toggleSentAlert(),
    {
      isLoading: sendingGoals,
      hasError:  errorSendingGoals,
    },
  );

  const sendGoals = React.useCallback(
    (
      comment: string,
      to: SendTo[],
    ) => dispatch(sendGoalsAction(
      projIds.projId,
      projIds.participantId,
      comment,
      to,
    )),
    [dispatch, projIds.projId, projIds.participantId],
  );

  const validationError = !!useError(ActionType.SendGoalsError);

  const sendTo = (email: string) => (
    [
      {
        toAddress:  email,
        languageId: props.languageId,
      },
    ]
  );

  const { handleSubmit, handleChange, isValid, values } = useFormik({
    initialValues:    {
      comment: '',
      email:   '',
    },
    onSubmit:         (values) => {
      sendGoals(values.comment, sendTo(values.email));
    },
    validationSchema: emailGoalsFormValidation,
    validateOnChange: false,
  });

  return (
    <fieldset disabled={validationError} css={[FIELDSET_DEFAULT, styles.root]}>
      <form onSubmit={handleSubmit}>
        {
          (goalsSent) && (
            <LocalDiv css={[FLEX_ROW, styles.msgRow]}>
              <FontAwesomeIcon icon={faCheck} css={css`color: ${COLORS.BLUE};`} size={'1x'} />
              <Spacer sizePx={10} />
              <Msg
                str={Str.GoalsSentTo}
                replace={{ 'email': values.email }}
                css={FLEX_COLUMN}
              />
            </LocalDiv>
          )
        }
        <LocalDiv css={styles.msgRow}>
          <Msg str={Str.SendGoals} />
        </LocalDiv>
        {
          (!isValid) && (
            <div
              css={[
                FLEX_ROW,
                ERROR_MSG,
                styles.msgRow,
                css`
                  align-items: center;
                `,
              ]}
            >
              <Msg str={Str.InvalidEmail} />
            </div>
          )
        }
        <LocalInput
          placeholderTranslation={Str.Email}
          name='email'
          css={[INPUT(), styles.input]}
          autoComplete={'off'}
          value={values.email}
          onChange={handleChange}
        />
        <LocalTextArea
          placeholderTranslation={Str.OptionalComments}
          name='comment'
          css={styles.textArea}
          autoComplete={'off'}
          value={values.comment}
          rows={4}
          onChange={handleChange}
        />
        <LocalBtn
          type='submit'
          css={styles.submitButton}
          str={Str.Send}
          disabled={!props.allAnswered}
        />
      </form>
    </fieldset>
  );
};

export default EmailGoalsForm;
